import {computed, ref, watch} from 'vue';
import type {UserIdentity, UserResource} from '@navbar-api';
import {UserIdentityTypeEnum} from '@navbar-api';
import {NavbarContext, useNavbarStateStore} from '@navbar-registry';
import cookie from 'js-cookie';
import {defineStore} from 'pinia';
import {navigateTo, useAuth, useGuestHost, useLogger} from '#imports';

export const useUserStore = defineStore('navbar-logged-user', () => {
    const user = ref<UserResource>();
    const navbarState = useNavbarStateStore();
    const logger = useLogger();
    const host = useGuestHost();

    const authStore = useAuth();

    // - Identities
    const userIdentities = computed<UserIdentity[]>(() => user.value?.identities ?? []);
    const selectedIdentityId = computed<string | undefined>(() => authStore.user?.selectedIdentity);
    const selectedIdentity = computed<UserIdentity | undefined>(() => userIdentities.value.find(({id}) => selectedIdentityId.value === id));
    const hasSelectedClientIdentity = computed<boolean>(() => selectedIdentity.value?.type === UserIdentityTypeEnum.Client);
    const hasSelectedFreelanceIdentity = computed<boolean>(() => selectedIdentity.value?.type === UserIdentityTypeEnum.Freelance);
    const hasSelectedSubcontractorIdentity = computed<boolean>(() => selectedIdentity.value?.type === UserIdentityTypeEnum.Subcontractor);

    const hasFreelancerIdentity = computed<boolean>(
        () => userIdentities.value && userIdentities.value.length > 0 && userIdentities.value.some((identity) => identity.type === 'FREELANCE'),
    );

    const hasSubcontractorIdentity = computed<boolean>(
        () =>
            userIdentities.value &&
            userIdentities.value.length > 0 &&
            userIdentities.value.some((identity) => identity.type === UserIdentityTypeEnum.Subcontractor),
    );

    const cachedIdentityRole = ref<UserIdentityTypeEnum>();

    watch(
        selectedIdentity,
        (newIdentity) => {
            if (import.meta.client) {
                const currentIdentityRole = sessionStorage.getItem('cachedIdentityRole');
                if (newIdentity) {
                    cachedIdentityRole.value = newIdentity.type;
                    sessionStorage.setItem('cachedIdentityRole', newIdentity.type);
                } else {
                    cachedIdentityRole.value = currentIdentityRole as UserIdentityTypeEnum;
                }
            }
        },
        {immediate: true},
    );

    async function refreshUser() {
        user.value = await NavbarContext.getUser();
    }

    async function setUserIdentity(identityId: string) {
        try {
            if (selectedIdentity.value?.id !== identityId) {
                cookie.set('hopsi', identityId);
                await NavbarContext.selectIdentity({selectIdentityRequest: {identityId}});
                sessionStorage.removeItem('cachedIdentityRole');
                if (navbarState.state.navbarDisplayMode) {
                    navigateTo(`${host}/dashboard`, {external: true});
                } else {
                    navigateTo(host, {external: true});
                }
            }
        } catch (e) {
            logger.error(e);
        }
    }

    function logoutUser() {
        authStore.logout();
    }

    // - Onboarding

    async function acknowledgeOnboarding(closeOnboarding = true) {
        if (closeOnboarding) {
            user.value!.options.onboarded = true;
        }
        await NavbarContext.acknowledgeOnboarding();
    }

    return {
        user,
        userIdentities,
        selectedIdentity,
        hasSelectedClientIdentity,
        hasSelectedFreelanceIdentity,
        hasSelectedSubcontractorIdentity,
        cachedIdentityRole,
        hasFreelancerIdentity,
        hasSubcontractorIdentity,
        refreshUser,
        setUserIdentity,
        logoutUser,
        acknowledgeOnboarding,
    };
});
